import { HOURGLASS_SERVICE, HourglassService } from "@cervval.js/client-html5-core/services";
import { OFFLINE_SERVICE, OfflineService } from "@cervval.js/client-html5-core/src/services/offline.service";
import { NAVIGATION_SERVICE, NavigationService } from "@cervval.js/client-html5-spa/navigation";
import { ButtonSize } from "@cervval.js/client-html5-ui/src/components/button/button";
import { DialogSize, showErrorDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { CjsDropdownButton, DropdownButtonItemClickEvent } from "@cervval.js/client-html5-ui/src/components/dropdownButton/dropdownButton";
import { getIconFaName } from "@cervval.js/client-html5-ui/src/components/icon/iconFa.tools";
import { IconFA6 } from "@cervval.js/client-html5-ui/src/components/icon/tools/fontAwesome6IconList.tools";
import { CLIENT_WEB_SOCKET_SERVICE, ClientWebSocketService } from "@cervval.js/client-websocket/src/clientWebSocket.service";
import { Action, ActionInfo, ActionParams } from "@cervval.js/common-action/src/action.model";
import { DefaultAction } from "@cervval.js/common-action/src/defaultAction";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { ACTION_CONTEXT_SERVICE, ActionContextService } from "@cervval.js/common-action/src/services/actionContext.service";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "@cervval.js/common-action/src/services/cache.service";
import { isDirty } from "@cervval.js/common-action/src/services/cache.tools";
import { AbstractActionService } from "@cervval.js/common-action/src/services/impl/abstract.action.service";
import { WS_ROOM_PREFIX } from "@cervval.js/common-websocket/src/webSocket.consts";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { ErrorWithCode, wrapError } from "@cervval.js/common/src/errors/errors";
import { anyReallyAny } from "@cervval.js/common/tools";
import { i18n } from "../i18n/i18n";

@Injectable(ACTION_SERVICE, { registerOnErrorMessageService: true })
export class ClientActionService<ElementTypes, Context> extends AbstractActionService<ElementTypes, Context> {

    @Injected(ACTION_CONTEXT_SERVICE, { optional: true })
    protected readonly _actionContextService?: ActionContextService<Context>;

    @Injected(ACTION_CACHE_SERVICE)
    public readonly actionCacheService!: ActionCacheService<ElementTypes>;

    @Injected(CLIENT_WEB_SOCKET_SERVICE, { optional: true })
    public readonly clientWebSocketService?: ClientWebSocketService;

    @Injected(OFFLINE_SERVICE, { optional: true })
    public readonly offlineService?: OfflineService;

    /** @inheritdoc */
    protected override _createAction<AppLogInfos>(params: ActionParams<ElementTypes, AppLogInfos, Context>): Action<ElementTypes, AppLogInfos, Context> {
        const context = params.context ?? this._actionContextService?.getContext();
        return new DefaultAction<ElementTypes, AppLogInfos, Context>(params, this.actionCacheService, context);
    }

    /** @inheritdoc */
    protected override _getGlobalCacheService(): ActionCacheService<ElementTypes> | undefined {
        return this.actionCacheService;
    }

    /**
     * Display error to user in a dialog if error is not handled
     */
    protected override _handleError(actions: ActionInfo<ElementTypes, Context>[], error: ErrorWithCode): Promise<boolean> {
        return super._handleError(actions, error).then((handled) => {
            if (!handled) {
                showErrorDialog({
                    error
                }, {
                    size: DialogSize.EXTRA_LARGE,
                    onAfterDialogHide: () => {
                        injector.get<NavigationService<string>>(NAVIGATION_SERVICE).refreshCurrentPage();
                    }
                });
                this.actionCacheService.cacheDirty.emit({ reason: "persistenceError" });
            }
            return true;
        });
    }

    /**
     * get current user socket uid
     */
    public override getUserSocketUid(): string | undefined {
        return this.clientWebSocketService?.getSocketUid();
    }

    /** get instance from injector (throws an error if dependency was not found) */
    public static get<ElementTypes, Context>(): ClientActionService<ElementTypes, Context> {
        return injector.get(ACTION_SERVICE);
    }


    /**
     * Adds a dropdown button and register it in the client websocket service
     */
    public static createRefreshButton<ElementTypeName = string>(options?: {
        /** override refresh */
        refreshPage?: () => void,
        /** override clear */
        clearCacheAndRefreshPage?: () => void,
        /** If you provide this function the refresh button style only depends on the dirty state of the current page */
        getCurrentPageElementTypes?: () => ElementTypeName[] | undefined,
        /** override refresh style */
        refreshStyleOnDirtyStateChange?: (btn: CjsDropdownButton) => void
    }): CjsDropdownButton {
        const _i18n = i18n();

        const refreshBtn = new CjsDropdownButton({
            splitToggleArrow: true,
            dropDirection: "bottom",
            icon: {
                name: getIconFaName(IconFA6.ROTATE)
            },
            size: ButtonSize.SMALL,
            dropDownItemConfigs: [
                {
                    value: "REFRESH",
                    label: _i18n.refreshBtn.label,
                    icon: {
                        name: getIconFaName(IconFA6.ROTATE),
                        fixedWidth: true
                    }
                },
                {
                    value: "CLEAR",
                    label: _i18n.clearBtn.label,
                    icon: {
                        name: getIconFaName(IconFA6.BROOM),
                        fixedWidth: true
                    }
                }
            ]
        });
        refreshBtn.components.dropdownMenu.classList.add("dropdown-menu-end");// Align menu on end
        refreshBtn.classList.add("action-refresh-btn");

        const callBack = (e?: CustomEvent<DropdownButtonItemClickEvent<string>>): void => {
            if (e?.detail.value === "CLEAR") {
                if (options?.clearCacheAndRefreshPage) {
                    options?.clearCacheAndRefreshPage();
                } else {
                    // default clear and refresh
                    injector.get<ActionCacheService<anyReallyAny>>(ACTION_CACHE_SERVICE).cacheDirty.emit({ reason: "USER_REQUEST" });
                    injector.get<NavigationService<anyReallyAny>>(NAVIGATION_SERVICE).refreshCurrentPage();
                }
            } else {
                if (options?.refreshPage) {
                    options?.refreshPage();
                } else {
                    // default refresh
                    injector.get<NavigationService<anyReallyAny>>(NAVIGATION_SERVICE).refreshCurrentPage();
                }
            }
        };
        refreshBtn.clickEvent.on(() => callBack());// refresh
        refreshBtn.itemClickEvent.on(callBack);// clear or refresh

        const refreshStyle = (): void => {
            if (options?.refreshStyleOnDirtyStateChange) {
                options?.refreshStyleOnDirtyStateChange(refreshBtn);
            } else {
                const cacheService = injector.get<ActionCacheService<anyReallyAny>>(ACTION_CACHE_SERVICE);
                if (isDirty(cacheService, options?.getCurrentPageElementTypes?.() as anyReallyAny[] | undefined)) {
                    refreshBtn.classList.add("cache-dirty");
                } else {
                    refreshBtn.classList.remove("cache-dirty");
                }
            }
        };

        // refresh button style on dirty state change
        injector.get<ActionCacheService<anyReallyAny>>(ACTION_CACHE_SERVICE).dirtyStateChanged.on(() => refreshStyle());

        return refreshBtn;
    }

    /**
     * Listens to persist queue and update save state icon 
     */
    public static configurePersistStatusLed(led: HTMLElement): void {
        const _i18n = i18n();
        const actionService = ClientActionService.get();

        const updateLed = (): void => {
            const stats = actionService.getPersistQueueStats();
            let status = "saved";
            let str = "";
            if (stats.operationsToPersist) {
                status = "pending";
                str += _i18n.pending({ operationCount: stats.operationsToPersist, actionCount: stats.actionsToPersist }) + "\n";
            }
            if (stats.operationsSendInProgress) {
                status = "sending";
                str += _i18n.sending({ operationCount: stats.operationsSendInProgress, actionCount: stats.actionsSendInProgress });
            }
            if (stats.operationsToPersist === 0 && stats.operationsSendInProgress === 0) {
                if (actionService.offlineService?.isOffline() === true) {
                    status = "offline";
                    str = _i18n.offline;
                } else {
                    str = _i18n.allSaved;
                }
            }
            led.setAttribute("status", status);
            led.setAttribute("title", str);
        };
        actionService.onPersistQueueInfoChange.on(updateLed);
        actionService.offlineService?.offlineStatusChange.on(updateLed);
    }

    /** Configure undo redo buttons (does not install undo redo keyboard bindings) */
    public static configureUndoRedoButtons(components: { redoBtn: HTMLButtonElement, undoBtn: HTMLButtonElement }, refreshApplication: () => void, p?: { waitForReply: boolean }): void {
        const _i18n = i18n();
        const actionService = ClientActionService.get();
        const hourglassService = injector.get<HourglassService<unknown>>(HOURGLASS_SERVICE);

        const _refreshButton = (nextAction: ActionInfo<unknown, unknown> | undefined, btn: HTMLButtonElement, prefix: string): void => {
            if (nextAction == null) {
                // nothing to undo/redo
                btn.setAttribute("disabled", "true");
                btn.removeAttribute("title");
            } else {
                // enable button and show action message in tooltip
                btn.removeAttribute("disabled");
                btn.setAttribute("title", `${prefix} : '${nextAction.action.params.logMessage}'`);
            }
        };

        /** enable or disabled buttons depending on undo / redo stack */
        const _refreshUndoRedoButtons = (): void => {
            _refreshButton(actionService.getNextActionToUndo(), components.undoBtn, _i18n.undo);
            _refreshButton(actionService.getNextActionToRedo(), components.redoBtn, _i18n.redo);
        };

        // refresh application after undo / redo and refresh undo / redo buttons after action process
        actionService.onAfterActionProcessed.on((e) => {
            _refreshUndoRedoButtons();
        });
        actionService.onClear.on((e) => {
            _refreshUndoRedoButtons();
        });
        // process undo on undoBtn click
        components.undoBtn.addEventListener("click", () => {
            hourglassService.encapsulate(() => {
                return p?.waitForReply ? actionService.undoAndWaitForReply() : actionService.undo();
            }).then(() => {
                refreshApplication();
            }).catch((e) => {
                showErrorDialog({ error: wrapError(e) });
                actionService.actionCacheService.cacheDirty.emit({ reason: "persistenceError" });
            });
        });
        // process redo on redoBtn click
        components.redoBtn.addEventListener("click", () => {
            hourglassService.encapsulate(() => {
                return p?.waitForReply ? actionService.redoAndWaitForReply() : actionService.redo();
            }).then(() => {
                refreshApplication();
            }).catch((e) => {
                showErrorDialog({ error: wrapError(e) });
                actionService.actionCacheService.cacheDirty.emit({ reason: "persistenceError" });
            });
        });

        // refresh undo / redo buttons now...
        _refreshUndoRedoButtons();
    }

    /** @inheritdoc */
    protected override submitAction<AppLogInfos, Result>(action: Action<ElementTypes, AppLogInfos, Context>, result: Result): Promise<Result> {

        const roomsToNotify = [...action.params.notifyRooms ?? [], ...this.clientWebSocketService?.getRoomsWithPrefix(WS_ROOM_PREFIX.AUTO_NOTIFICATION) ?? []];
        action.params.notifyRooms = roomsToNotify;

        return super.submitAction(action, result);
    }
}